/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import {Dialog, DialogContent} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {deleteSFTPServer} from '@ion/core/src/services/Reports';
import {AccountLink, GenericObject} from '@ion/core/src/types/entities';
import QueryKey from '@ion/query/src/config/QueryKeys';
import Breadcrumb from '../components/breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../components/breadcrumbs/BreadcrumbUtils';
import {IconButton} from '../components/buttons/IconButton';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useIsDesktop} from '../hooks/useMedia';
import {ColumnChooserIcon} from '../icons/ColumnChooserIcon';
import {DeleteIcon} from '../icons/DeleteIcon';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsAlt, dlsColor, dlsTypography} from '../theme/theme';
import {IconSize} from '../types/types';
import SFTPConfigurationDetailViewGrid from './grids/SFTPConfigurationDetailViewGrid';
import ConfirmationPrompt from './ConfirmationPrompt';
import styles, {getClasses} from './editorStyles';

/**
 * This interface defines the properties for the SFTP Configuration
 * Detail View component.
 */
export interface SFTPConfigurationDetailViewProps {
  account: AccountLink;
  onCancel?: (event) => void;
  onSelection?: Function;
  isCurrentTheme?: boolean;
  v2?: boolean;
};

// defines the override styles for the SFTPConfigurationDetailView component...
const styleOverrides = (theme) => ({
  formWrapper : {
    overflowY                      : 'auto',
    [theme.breakpoints.down('sm')] : {
      padding : '0 20px'
    }
  },
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  paddingNone : {
    display                        : 'flex',
    flexDirection                  : 'column',
    alignItems                     : 'center',
    backgroundColor                : toggleThemeStyle(dlsColor('grey6'), dlsColor('white')),
    height                         : 'calc(100vh - 248px)',
    padding                        : 0,
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 80px)'
    }
  },
  label : {
    ...dlsTypography('h1'),
    fontSize      : '20px',
    textTransform : 'uppercase'
  },
  chevron : {
    fill : dlsColor('grey3')
  },
  activeCrumb : {
    color : dlsColor('grey1')
  },
  inactiveCrumb : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  root : {
    display       : 'flex',
    overflow      : toggleThemeStyle(undefined, 'hidden'),
    flexDirection : 'column',
    width         : toggleThemeStyle('87%', '98%'),
    height        : '100%',
    margin        : toggleThemeStyle('18px', '18px 0 0px 10px')
  },
  title : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase'
  },
  describtion : {
    ...dlsAlt('typography', 'label2'),
    fontSize      : '14px',
    lineHeight    : '20px',
    letterSpacing : '0px',
    fontFamily    : 'Roboto'
  },
  gridContainer : {
    display         : 'flex',
    flexDirection   : 'column',
    width           : '100%',
    height          : toggleThemeStyle('85%', undefined),
    flex            : toggleThemeStyle(undefined, '1'),
    minHeight       : toggleThemeStyle(undefined, 0),
    marginTop       : '18px',
    backgroundColor : dlsColor('white')
  },
  gridHeader : {
    display        : 'flex',
    flexDirection  : 'row',
    width          : '98%',
    margin         : '0 8px',
    borderBottom   : toggleThemeStyle(`1px solid ${dlsColor('grey4')}`, 'none'),
    justifyContent : 'space-between',
    alignItems     : 'center'
  },
  headerLeft : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-around'
  },
  actionContainer : {
    width          : '100%',
    display        : 'flex',
    flexShrink     : toggleThemeStyle(undefined, 0),
    padding        : toggleThemeStyle(undefined, '16px 0'),
    borderTop      : `1px solid ${dlsColor('grey4')}`,
    justifyContent : 'end',
    alignItems     : 'center'
  },
  primaryButton : {
    width : '128px'
  }
});

/**
 * The SFTPConfigurationDetailView component is a component designed to provide a
 * comprehensive and detailed view of SFTP connection configurations. This component
 * is specifically crafted to offer users an interface that allows them to examine
 * and manage various aspects of SFTP configurations associated with a particular account.
 *
 * ```typescript
 *
 *  import {SFTPConfigurationDetailView} from './SFTPConfigurationDetailView';
 *
 *  <SFTPConfigurationDetailView account={account} onSelection={onSelection} />
 *
 * ```
 * @param account - The associated account link for the configuration.
 * @param onCancel - A callback function triggered when the user cancels an action.
 * @param onSelection - A general callback function for handling selection events.
 */
export const SFTPConfigurationDetailView = ({
  account,
  onCancel,
  onSelection,
  isCurrentTheme = false,
  v2 = false
}: SFTPConfigurationDetailViewProps) => {
  const {timeout}                     = useAnimatedMount();
  const editorStyles                  = useExtendStyles(styles, styleOverrides);
  const classes                       = editorStyles();
  const gridRef                       = useRef<GenericObject>();
  const isDesktop                     = useIsDesktop();
  const queryClient                   = useQueryClient();
  const [rowSelected, setRowSelected] = useState([]);

  const [showPrompt, setShowPrompt]       = useState(false);
  const [isLoading, setIsLoading]         = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState(false);

  const onDeleteConfigurations = useCallback(async() => {
    setDeleteSuccess(false);
    setDeleteFailure(false);
    setIsLoading(true);
    const accountId = entityId(account);
    try {
      // Parallel execution has some issues with the delete functionality.
      // For now, we have just implemented sequential calls to delete.
      for (const severConfig of rowSelected) {
        await (v2
          ? deleteSFTPServer(severConfig.id)
          : deleteSFTPServer(accountId, severConfig.name));
      }
      setDeleteSuccess(true);
    } catch (err) {
      setDeleteFailure(true);
    } finally {
      queryClient.invalidateQueries([QueryKey.GET_SFTP_CONFIGURATIONS, accountId, v2]);
      setIsLoading(false);
      setShowPrompt(false);
      setRowSelected([]);
    }
  }, [rowSelected, account, queryClient, v2]);

  const onCancelPrompt = useCallback(() => {
    setShowPrompt(false);
  }, []);

  const crumbData = useMemo(() => defineBreadcrumbs({
    crumbs : [{
      label   : translate('admin-groups:sftpEditor.editor.title'),
      click   : true,
      onClick : onCancel
    }, {
      label : translate('admin-groups:sftpEditor.grid.ctaText'),
      click : false
    }],
    classes : {
      activeCrumb   : classes.activeCrumb,
      inactiveCrumb : classes.inactiveCrumb
    }
  }) as any, [onCancel]);

  const handleSelect = useCallback(() => {
    onSelection?.(rowSelected[0]);
  }, [rowSelected, onSelection]);

  const onToggleColumnChooser = useCallback(() => {
    gridRef?.current?.toggleColumnChooser?.(true);
  }, []);

  const onDeleteInitialiation = useCallback(() => {
    setShowPrompt(true);
  }, []);

  return (
    <>
      <Dialog disableEnforceFocus open
        transitionDuration={timeout}
        classes={getClasses(classes)}>
        <DialogContent className={classes.paddingNone}>
          <div className={classes.root}>
            <div className={classes.title}>
              {translate('admin-groups:sftpEditor.editor.title')}
            </div>
            <div className={classes.describtion}>
              {translate('admin-groups:sftpEditor.editor.description')}
            </div>
            <div className={classes.gridContainer}>
              <div className={classes.gridHeader}>
                <Breadcrumb icon={<ChevronLeft className={classes.chevron} />}
                  height={'30px'}
                  iconClick={onCancel}>
                  {crumbData}
                </Breadcrumb>
                <div className={classes.headerLeft}>
                  <IconButton icon={<DeleteIcon size={IconSize.medium} />}
                    onClick={onDeleteInitialiation}
                    disabled={!rowSelected?.length} />
                  {isDesktop && (
                    <IconButton icon={<ColumnChooserIcon size={IconSize.medium} />}
                      onClick={onToggleColumnChooser} />
                  )}
                </div>
              </div>
              <SFTPConfigurationDetailViewGrid account={account}
                v2={v2}
                onSelectionChange={setRowSelected}
                isCurrentTheme={isCurrentTheme}
                ref={gridRef} />
              <div className={classes.actionContainer}>
                <TertiaryButton onClick={onCancel}>
                  {translate('common:editors.shared.cancel')}
                </TertiaryButton>
                <div className={classes.primaryButton}>
                  <PrimaryButton disabled={rowSelected?.length !== 1}
                    onClick={handleSelect}
                    fullWidth>
                    {translate('common:editors.shared.select')}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationPrompt title={translate('admin-groups:sftpEditor.delete.title')}
        successMessage={translate('admin-groups:sftpEditor.delete.successMessage')}
        failureMessage={translate('admin-groups:sftpEditor.editor.failureMessage')}
        secondaryActionlabel={translate('common:editors.delete.cancel')}
        primaryActionLabel={translate('common:editors.shared.continue')}
        message={translate('admin-groups:sftpEditor.delete.message')}
        onPrimaryAction={onDeleteConfigurations}
        onSecondaryAction={onCancelPrompt}
        isSuccess={deleteSuccess}
        isFailure={deleteFailure}
        loading={isLoading}
        show={showPrompt} />
    </>
  );
};

export default SFTPConfigurationDetailView;
